import { api } from '@/api/client'
import { BASE_MEU_ROBO, useMeuRobo } from '@/api/robo'
/**
 * ConversasTab — lista de conversas humanas do usuário (DMs + grupos de Organização).
 *
 * Funcionalidades:
 *  - Exibe todas as HumanConversations (tipo 'dm', 'org' ou 'chamado') ordenadas por atividade.
 *  - Badge de não lidos (unread > 0).
 *  - Botão "Nova conversa" abre o NewChatDrawer para iniciar DM ou abrir grupo de Org.
 *  - Estados: carregando, lista vazia, lista com itens.
 */
import { useState } from 'react'
import {
  Box,
  Stack,
  Group,
  Avatar,
  Text,
  Badge,
  Loader,
  Button,
  UnstyledButton,
} from '@mantine/core'
import { IconMessages, IconPencilPlus, IconRobot } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useHumanConversations } from '@/hooks/useChatDirectory'
import { chatDirectoryApi } from '@/api/chatDirectory'
import { EmptyState } from '@/components/ui/EmptyState'
import { EstadoDeConsulta } from '@/components/ui/EstadoDeConsulta'
import { NewChatDrawer } from './NewChatDrawer'
import { ConsultasAoDba } from './ConsultasAoDba'

function getInitials(title: string): string {
  const parts = (title || '').trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export default function ConversasTab() {
  const navigate = useNavigate()
  // Uma consulta no lugar de tenant-fixo + permissao: o contato so aparece
  // para quem TEM robo, e ja nomeado. Quem tem acesso ao TecPag-PRD e nenhum
  // robo via aqui um contato que nao existia.
  const { robo } = useMeuRobo()
  const showFe = !!robo && !!robo.owner_named_at
  const conversas = useHumanConversations()
  const data = conversas.data
  const [drawer, setDrawer] = useState(false)
  const [openingAtendente, setOpeningAtendente] = useState(false)
  const [openingFe, setOpeningFe] = useState(false)
  const [roboError, setRoboError] = useState(false)
  const openFe = async () => {
    if (!robo || openingFe) return
    setOpeningFe(true); setRoboError(false)
    try {
      const result = await api.post<{ conversation_id: number }>(`${BASE_MEU_ROBO}/conversation`)
      navigate(`/chat/${result.data.conversation_id}`)
    } catch { setRoboError(true) }
    finally { setOpeningFe(false) }
  }

  // @Atendente é um contato fixo (bot is_bot) no topo. Abre a DM 1:1 com ele,
  // onde o envio vai via /ask (anexos + cartões de ação de chamado).
  const { data: people } = useQuery({
    queryKey: ['chat-directory-people'],
    queryFn: chatDirectoryApi.people,
    staleTime: 5 * 60 * 1000,
  })
  const atendente = (people ?? []).find((p) => p.is_bot)

  const openAtendente = async () => {
    if (!atendente || openingAtendente) return
    setOpeningAtendente(true)
    try {
      const cid = await chatDirectoryApi.openDm(atendente.user_id)
      navigate(`/chat/${cid}`)
    } finally {
      setOpeningAtendente(false)
    }
  }

  // A DM 1:1 com o @Atendente já é representada pelo contato fixo do topo — não
  // repetir como linha "Conversa direta" na lista (evita o @Atendente duplicado).
  // O bot tem nome reservado ('Atendente'); o título de uma DM é o nome do par.
  const atendenteName = atendente?.name
  const items = (data ?? [])
    .filter(c => !c.is_robo_dm)
    .filter((c) => !(c.conv_type === 'dm' && !!atendenteName && c.title === atendenteName))
    .slice()
    .sort((a, b) => {
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0
      return tb - ta
    })

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Cabeçalho com botão Nova conversa */}
      <Box px="md" pt="md" pb="xs" style={{ flexShrink: 0 }}>
        <Button
          leftSection={<IconPencilPlus size={16} />}
          onClick={() => setDrawer(true)}
          fullWidth
          variant="light"
          color="inovareTeal"
        >
          Nova conversa
        </Button>
      </Box>

      {/* Corpo rolável */}
      <Box style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        {showFe && <UnstyledButton onClick={openFe} disabled={openingFe} px="md" py="sm" style={{ width: '100%', borderBottom: '1px solid var(--ai-border-light)' }}><Group gap="sm"><Avatar radius="md" color="inovareTeal"><IconRobot /></Avatar><Box><Text fw={700}>{robo?.name}</Text><Text size="xs" c="dimmed">Alertas de {robo?.tenant_name ?? ''} · o seu robô</Text></Box>{openingFe && <Loader size="xs" />}</Group></UnstyledButton>}
        {roboError && <Text c="red" size="sm" px="md">Não foi possível abrir a conversa com {robo?.name}. Tente novamente.</Text>}
        {/* @Atendente — contato fixo no topo (abre a DM 1:1 com o assistente) */}
        {atendente && (
          <UnstyledButton
            onClick={openAtendente}
            px="md"
            py="sm"
            style={{
              width: '100%',
              borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
              background: 'var(--ai-bg-subtle)',
              transition: 'background 0.15s',
            }}
          >
            <Group gap="sm" wrap="nowrap">
              <Avatar radius="md" size={44} color="inovareTeal" variant="filled" style={{ flexShrink: 0 }}>
                <IconRobot size={24} />
              </Avatar>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={700} truncate>
                  @Atendente
                </Text>
                <Text size="xs" c="dimmed">
                  Assistente — tire dúvidas, anexe arquivos, abra chamados
                </Text>
              </Box>
              {openingAtendente && <Loader size="xs" color="inovareTeal" style={{ flexShrink: 0 }} />}
            </Group>
          </UnstyledButton>
        )}

        {/* As conversas do @DBA abertas pelo editor SQL: à parte, recolhidas (consoleai#26071). */}
        <ConsultasAoDba />

        {/* Antes: `data ?? []` sem ramo de erro. Com a listagem fora do ar a aba dizia "Nenhuma
          conversa ainda" e mandava criar uma — para quem podia ter dezenas esperando resposta. */}
        <EstadoDeConsulta
          consulta={conversas}
          carregando={
            <Stack align="center" pt="xl">
              <Loader size="sm" color="inovareTeal" />
            </Stack>
          }
          vazio={items.length === 0}
          estadoVazio={
            <EmptyState
              icon={IconMessages}
              title="Nenhuma conversa ainda"
              description="Toque em Nova conversa para falar com uma pessoa ou abrir o grupo de uma Organização."
              size="md"
            />
          }
        >
        <Stack gap={0}>
          {items.map((c) => (
            <UnstyledButton
              key={c.id}
              onClick={() => navigate(`/chat/${c.id}`)}
              px="md"
              py="sm"
              style={{
                borderBottom: '1px solid var(--ai-border-light, var(--mantine-color-gray-2))',
                background: c.unread > 0
                  ? 'var(--ai-bg-subtle)'
                  : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              <Group gap="sm" wrap="nowrap">
                {/* Avatar: foto do usuário (DM) / logo do grupo, com fallback
                    em iniciais. O backend já resolve avatar_url (foto ou logo
                    da org); o src estava faltando — só iniciais apareciam. */}
                <Avatar
                  radius="md"
                  size={44}
                  src={c.avatar_url ?? undefined}
                  color={c.conv_type === 'dm' ? 'blue' : 'inovareTeal'}
                  variant="filled"
                  style={{ flexShrink: 0 }}
                >
                  {getInitials(c.title)}
                </Avatar>

                {/* Corpo: título + subtítulo */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    size="sm"
                    fw={c.unread > 0 ? 700 : 500}
                    truncate
                  >
                    {c.title}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {c.conv_type === 'org'
                      ? 'Grupo da Organização'
                      : c.conv_type === 'chamado'
                        ? 'Atendimento de chamado'
                        : 'Conversa direta'}
                  </Text>
                </Box>

                {/* Badge de não lidos */}
                {c.unread > 0 && (
                  <Badge color="inovareTeal" variant="filled" size="sm" style={{ flexShrink: 0 }}>
                    {c.unread}
                  </Badge>
                )}
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
        </EstadoDeConsulta>
      </Box>

      <NewChatDrawer opened={drawer} onClose={() => setDrawer(false)} />
    </Box>
  )
}
